import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapContainer, ImageOverlay, Marker, Polygon, Tooltip, useMap, useMapEvent } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { X } from 'lucide-react'
import { ROUTES } from '@app/router/routes'
import { useLanguage } from '@shared/contexts/LanguageContext'
import { useRotatedMapDrag } from '@shared/hooks/useRotatedMapDrag'
import { getCoordinateList } from '../api/transmissionService'
import { ZONES, coordinateIdToTableId } from '../mapZones'

// area_2.jpg 原始像素尺寸
const IMG_W = 1450
const IMG_H = 854
const IMAGE_BOUNDS = [[0, 0], [IMG_H, IMG_W]]
const POINT_RADIUS = 30
// 圓點視覺大小（固定螢幕像素，不隨地圖 zoom 縮放，確保字體始終清楚可讀）
const MARKER_DIAMETER = 44
// zoom 達到此門檻後，桌位標籤文字放大，方便近看時閱讀；
// 同時也是 area_2.2.jpg（遠景／法水長流分區導覽）跟 area_2.jpg（近景／81 桌位細部）的切換門檻，
// zoom 在門檻以下顯示 area_2.2.jpg，以上（含）顯示 area_2.jpg
const LABEL_ZOOM_BREAKPOINT = -0.15
const LABEL_FONT_SIZE_LARGE = 20

// Leaflet 座標是 [y, x]（lat, lng），y 軸從圖片底部往上算
const toLatLng = (x, y) => [IMG_H - y, x]

// area_2.2.jpg 原始像素尺寸（跟 area_2.jpg 取景範圍相同，只是解析度跟長寬比不完全一致），
// 疊圖時仍套用同一組 IMAGE_BOUNDS（等同 area_2.jpg 尺寸），所以座標要先用比例換算成 area_2.jpg 的像素空間
const OVERVIEW_IMG_W = 2026
const OVERVIEW_IMG_H = 1197
const overviewToLatLng = (x, y) => toLatLng((x / OVERVIEW_IMG_W) * IMG_W, (y / OVERVIEW_IMG_H) * IMG_H)
const overviewBoxToPolygon = (x1, y1, x2, y2) => [
  overviewToLatLng(x1, y1),
  overviewToLatLng(x2, y1),
  overviewToLatLng(x2, y2),
  overviewToLatLng(x1, y2),
]

// area_2.2.jpg 上「弘法・現況」烤入圖片的標籤範圍（無法用程式移除，改用透明熱區疊在上面點擊）；
// 行為跟其他展區熱區一致：navigate 到展區二介紹頁，不是打開細部地圖（此時已經在細部地圖裡了）
const HONGFA_LABEL_BOX = [750, 563, 1386, 811]

// area_2.2.jpg 上法／水／長／流四個分區聚合點位（不是個別桌位，只是分區代表座標），
// 點擊後 zoom in 飛到該分區，跟頂部分區按鈕（ZONES.map）共用同一套 flyToZone 邏輯
const OVERVIEW_ZONE_POINTS = {
  fa: [1624, 883],
  shui: [1604, 364],
  chang: [600, 368],
  liu: [545, 905],
}
const OVERVIEW_ZONE_COLOR = '#44842d'
// 純文字標籤用的透明零尺寸圖示，只借 Marker 定位，實際顯示交給 Tooltip 氣球
const EMPTY_ICON = L.divIcon({ className: '', html: '', iconSize: [0, 0] })

// 展區二桌位點位（依 area_2.jpg 逐一標記，中心點 ± 固定半徑）
const TABLES = [
  { id: 'table1', point: [257, 147] },
  { id: 'table2', point: [275, 100] },
  { id: 'table3', point: [339, 77] },
  { id: 'table4', point: [275, 237] },
  { id: 'table5', point: [369, 182] },
  { id: 'table6', point: [421, 161] },
  { id: 'table7', point: [462, 191] },
  { id: 'table8', point: [518, 154] },
  { id: 'table9', point: [607, 108] },
  { id: 'table10', point: [649, 128] },
  { id: 'table11', point: [698, 81] },
  { id: 'table12', point: [369, 290] },
  { id: 'table13', point: [360, 338] },
  { id: 'table14', point: [420, 366] },
  { id: 'table15', point: [491, 259] },
  { id: 'table16', point: [546, 230] },
  { id: 'table17', point: [581, 266] },
  { id: 'table18', point: [550, 338] },
  { id: 'table19', point: [596, 357] },
  { id: 'table20', point: [855, 224] },
  { id: 'table21', point: [904, 198] },
  { id: 'table22', point: [944, 226] },
  { id: 'table23', point: [999, 190] },
  { id: 'table24', point: [1089, 136] },
  { id: 'table25', point: [1137, 93] },
  { id: 'table26', point: [1183, 114] },
  { id: 'table27', point: [1298, 86] },
  { id: 'table28', point: [895, 329] },
  { id: 'table29', point: [939, 318] },
  { id: 'table30', point: [1104, 224] },
  { id: 'table31', point: [1200, 186] },
  { id: 'table32', point: [1247, 167] },
  { id: 'table33', point: [1308, 187] },
  { id: 'table34', point: [1350, 160] },
  { id: 'table35', point: [1011, 356] },
  { id: 'table36', point: [1051, 303] },
  { id: 'table37', point: [1099, 322] },
  { id: 'table38', point: [1218, 288] },
  { id: 'table39', point: [1280, 306] },
  { id: 'table40', point: [1326, 275] },
  { id: 'table41', point: [1090, 408] },
  { id: 'table42', point: [225, 457] },
  { id: 'table43', point: [244, 500] },
  { id: 'table44', point: [304, 496] },
  { id: 'table45', point: [225, 594] },
  { id: 'table46', point: [314, 605] },
  { id: 'table47', point: [355, 560] },
  { id: 'table48', point: [410, 585] },
  { id: 'table49', point: [445, 531] },
  { id: 'table50', point: [521, 583] },
  { id: 'table51', point: [572, 537] },
  { id: 'table52', point: [243, 684] },
  { id: 'table53', point: [296, 722] },
  { id: 'table54', point: [346, 704] },
  { id: 'table55', point: [441, 665] },
  { id: 'table56', point: [499, 646] },
  { id: 'table57', point: [527, 685] },
  { id: 'table58', point: [926, 603] },
  { id: 'table59', point: [940, 555] },
  { id: 'table60', point: [1001, 529] },
  { id: 'table61', point: [1128, 522] },
  { id: 'table62', point: [1133, 567] },
  { id: 'table63', point: [1193, 584] },
  { id: 'table64', point: [1240, 520] },
  { id: 'table65', point: [1279, 486] },
  { id: 'table66', point: [1347, 497] },
  { id: 'table67', point: [747, 676] },
  { id: 'table68', point: [773, 718] },
  { id: 'table69', point: [831, 703] },
  { id: 'table70', point: [976, 694] },
  { id: 'table71', point: [979, 737] },
  { id: 'table72', point: [1040, 755] },
  { id: 'table73', point: [1065, 660] },
  { id: 'table74', point: [1133, 664] },
  { id: 'table75', point: [1142, 709] },
  { id: 'table76', point: [1211, 711] },
  { id: 'table77', point: [1274, 618] },
  { id: 'table78', point: [1331, 624] },
  { id: 'table79', point: [1242, 770] },
  { id: 'table80', point: [1298, 746] },
  { id: 'table81', point: [1336, 786] },
]

// 依分區點位範圍 + padding 算出像素座標邊界
const buildZoneBoundsMap = (padding) =>
  Object.fromEntries(
    ZONES.map(zone => {
      const [start, end] = zone.range
      const points = TABLES.slice(start - 1, end).map(t => t.point)
      const xs = points.map(p => p[0])
      const ys = points.map(p => p[1])
      const minX = Math.min(...xs) - padding
      const maxX = Math.max(...xs) + padding
      const minY = Math.min(...ys) - padding
      const maxY = Math.max(...ys) + padding
      return [zone.id, [toLatLng(minX, maxY), toLatLng(maxX, minY)]]
    })
  )

// 供 flyToBounds 使用，padding 較大讓框選後畫面留白舒適
const ZONE_BOUNDS = buildZoneBoundsMap(80)
// 供 ZoneDetector 判斷「地圖中心點屬於哪個分區」使用，padding 縮小避免框選範圍過大
const ZONE_DETECT_BOUNDS = buildZoneBoundsMap(30)
// 分區矩形中心點（給直向模式用固定 zoom 飛過去，見 ZoneFlyTo）
const ZONE_CENTERS = Object.fromEntries(
  Object.entries(ZONE_BOUNDS).map(([id, [[lat1, lng1], [lat2, lng2]]]) => [id, [(lat1 + lat2) / 2, (lng1 + lng2) / 2]])
)
// 地圖正中央手動畫定的「不屬於任何分區」地帶（point-picker 方框模式標出的像素座標 [x1, y1, x2, y2]），
// 地圖中心點落在這個範圍內時，直接判定不屬於任何分區，優先於下方四個分區的判斷
const NO_ZONE_BOUNDS_PX = [646, 364, 865, 577]
const NO_ZONE_BOUNDS = [toLatLng(NO_ZONE_BOUNDS_PX[0], NO_ZONE_BOUNDS_PX[3]), toLatLng(NO_ZONE_BOUNDS_PX[2], NO_ZONE_BOUNDS_PX[1])]


function useIsTouchDevice() {
  const [isTouch, setIsTouch] = useState(() => window.matchMedia('(pointer: coarse)').matches)

  useEffect(() => {
    const mql = window.matchMedia('(pointer: coarse)')
    const handler = (e) => setIsTouch(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return isTouch
}

// 手機直向時，把整層強制用 CSS 轉 90 度顯示成橫版（不依賴裝置實際轉動，
// 也不呼叫 Fullscreen API／orientation.lock，純視覺旋轉，所有裝置都有效）
function usePortraitOrientation() {
  const [isPortrait, setIsPortrait] = useState(() => window.matchMedia('(orientation: portrait)').matches)

  useEffect(() => {
    const mql = window.matchMedia('(orientation: portrait)')
    const handler = (e) => setIsPortrait(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return isPortrait
}

// 展區二細部地圖初始 zoom 級距，依裝置目前是直向還是橫向分開設定，方便個別微調
const ZONE_DETAIL_ZOOM_OFFSET_PORTRAIT = -0.65
const ZONE_DETAIL_ZOOM_OFFSET_LANDSCAPE = -0.1

// 點分區按鈕飛入時，比 maxZoom 往下扣的量，直橫向分開設定；數字越小飛入後 zoom 越大（放得越近）
const ZONE_FLY_ZOOM_DROP_PORTRAIT = 0.1
const ZONE_FLY_ZOOM_DROP_LANDSCAPE = 0.38

// 手機版容器又窄又矮，fitBounds 塞滿容器會裁掉圖片上下或左右；
// offset 改變（例如裝置旋轉切換直向／橫向的預設值）時，以 fitBounds 算出的原始 zoom 為基準重新套用，
// 避免對著已經套過 offset 的 zoom 再疊加一次。
// skipInitial：掛載當下若已經指定要飛去某個分區（flyToZone 有初始值），
// 這裡就不要搶著把 zoom 設回「整圖置中」的初始基準，否則會跟緊接著的 flyToBounds 打架，
// 動畫飛過去後又被防礙 —— flyToBounds 本身就會算出合適的 zoom，不需要再疊加這個 offset
// 如果 shouldKeepMax 為 true，則不套用 offset 且設定 minZoom 限制使用者縮小地圖。
// 直向時寬度貼齊手機寬度，橫向時顯示整張圖不裁切。
function MapZoomLimitHandler({ shouldKeepMax, initialZoomOffset, skipInitial = false }) {
  const map = useMap()

  useEffect(() => {
    const getTargetZoom = () => {
      const container = map.getContainer()
      const containerWidth = container.clientWidth
      const containerHeight = container.clientHeight
      const isPortraitMode = containerHeight > containerWidth

      if (shouldKeepMax && containerWidth) {
        if (isPortraitMode) {
          // 直向：寬度貼齊手機寬度
          return Math.log2(containerWidth / IMG_W)
        } else {
          // 橫向：高度貼齊手機高度
          return Math.log2(containerHeight / IMG_H)
        }
      }
      return null
    }

    const updateZoomLimits = () => {
      const targetZoom = getTargetZoom()
      if (targetZoom !== null) {
        map.setMinZoom(targetZoom)
        if (map.getZoom() < targetZoom) {
          map.setZoom(targetZoom, { animate: false })
        }
      } else {
        map.setMinZoom(-2)
      }
    }

    const targetZoom = getTargetZoom()
    if (targetZoom !== null) {
      map.setMinZoom(targetZoom)
      if (!skipInitial) {
        map.setZoom(targetZoom, { animate: false })
      }
    } else {
      map.setMinZoom(-2)
      if (!skipInitial) {
        const fitZoom = map.getBoundsZoom(IMAGE_BOUNDS, true)
        if (initialZoomOffset) {
          map.setZoom(fitZoom + initialZoomOffset, { animate: false })
        }
      }
    }

    map.on('resize', updateZoomLimits)
    return () => {
      map.off('resize', updateZoomLimits)
    }
  }, [map, shouldKeepMax, initialZoomOffset, skipInitial])

  return null
}

// 容器尺寸變化時要通知 Leaflet 重新量測，否則熱區座標會對不上畫面
function MapResizeHandler() {
  const map = useMap()

  useEffect(() => {
    const container = map.getContainer()
    const invalidate = () => map.invalidateSize()
    const observer = new ResizeObserver(() => invalidate())
    observer.observe(container)
    window.addEventListener('orientationchange', invalidate)
    return () => {
      observer.disconnect()
      window.removeEventListener('orientationchange', invalidate)
    }
  }, [map])

  return null
}

// CSS 強制橫向旋轉時，修正 Leaflet 拖曳方向跟視覺滑動方向對不上的問題
function RotatedDragHandler({ enabled }) {
  const map = useMap()
  useRotatedMapDrag(map, { enabled })
  return null
}

// 用 divIcon 畫出精舍簡稱文字點位，取代原本純色的 Circle。
// 無背景形狀，靠白色描邊（多重 text-shadow）確保文字在任何底圖顏色上都看得清楚。
const TEXT_OUTLINE = '-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff, 0 1px 3px rgba(0,0,0,0.35)'
const buildTableIcon = (label, isHovered, isClickable, isZoomedIn) => L.divIcon({
  className: '',
  html: `<div style="
    width: ${MARKER_DIAMETER}px;
    height: ${MARKER_DIAMETER}px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #BC313D;
    font-weight: 700;
    font-size: ${isZoomedIn ? LABEL_FONT_SIZE_LARGE : (isHovered ? 15 : 13)}px;
    line-height: 1;
    text-shadow: ${TEXT_OUTLINE};
    white-space: nowrap;
    cursor: ${isClickable ? 'pointer' : 'default'};
  ">${label ?? ''}</div>`,
  iconSize: [MARKER_DIAMETER, MARKER_DIAMETER],
  iconAnchor: [MARKER_DIAMETER / 2, MARKER_DIAMETER / 2],
})

// 從報導頁定位回來時，疊加在目標桌位名稱下方的上下跳動指示圖示
const TARGET_ICON_SIZE = 30
const buildTargetIcon = () => L.divIcon({
  className: '',
  html: `<svg class="table-target-marker" width="${TARGET_ICON_SIZE}" height="${TARGET_ICON_SIZE}" viewBox="0 0 24 24" fill="#BC313D" style="display: block;">
    <path d="M12 2C7.58 2 4 5.58 4 10c0 5.25 7.14 11.34 7.45 11.6a.85.85 0 0 0 1.1 0C12.86 21.34 20 15.25 20 10c0-4.42-3.58-8-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
  </svg>`,
  iconSize: [TARGET_ICON_SIZE, TARGET_ICON_SIZE],
  // 錨點設在圖示底部（尖端）置中，只留一點點間距，讓圖示尖端緊貼桌位名稱正上方；
  // 這個偏移量是固定螢幕像素、不隨地圖 zoom 縮放，數值越大在 zoom out 時視覺上離文字越遠，故盡量縮小
  iconAnchor: [TARGET_ICON_SIZE / 2, TARGET_ICON_SIZE + 4],
})

// 點擊分區按鈕時，把地圖飛到該分區的中心點並放大到 maxZoom；地圖本身隨時保持可自由拖曳/縮放，
// 飛過去後不鎖定，使用者可以繼續自由移動到其他分區。
// flyToken 每次點擊都會遞增，即使連續點同一顆分區按鈕（zoneId 不變）也能重新觸發飛行動畫。
// 改用固定 zoom 飛到分區中心點，不透過 flyToBounds／fitBounds 自動計算 zoom——
// 分區矩形（ZONE_BOUNDS）偏寬（依 area_2.jpg 橫向底圖算出），容器窄高比跟矩形寬高比落差大時
// （尤其手機直向），fitBounds 系列方法會被容器較窄的那個維度卡住，算出的 zoom 明顯偏小、留白過多
function ZoneFlyTo({ flyToZone, flyToken, isPortrait }) {
  const map = useMap()

  useEffect(() => {
    if (flyToZone) {
      const zoomDrop = isPortrait ? ZONE_FLY_ZOOM_DROP_PORTRAIT : ZONE_FLY_ZOOM_DROP_LANDSCAPE
      map.flyTo(ZONE_CENTERS[flyToZone], map.getMaxZoom() - zoomDrop, { animate: true, duration: 0.75 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flyToken, map])

  return null
}

// 判斷點是否落在分區邊界內（bounds 為 [[minLat,minLng],[maxLat,maxLng]]）
function isLatLngInBounds([lat, lng], [[minLat, minLng], [maxLat, maxLng]]) {
  return lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng
}

// 持續偵測地圖中心點目前落在哪個分區內，讓上方分區按鈕即時反映使用者目前所在位置。
// 中心點落在正中央手動畫定的 NO_ZONE_BOUNDS 內時優先判定為不屬於任何分區；
// 不在任何分區範圍內時同樣回報 null，讓按鈕全部不高亮
function ZoneDetector({ onZoneChange }) {
  const map = useMap()

  useEffect(() => {
    const detect = () => {
      const center = map.getCenter()
      const point = [center.lat, center.lng]
      if (isLatLngInBounds(point, NO_ZONE_BOUNDS)) {
        onZoneChange(null)
        return
      }
      const zone = ZONES.find(z => isLatLngInBounds(point, ZONE_DETECT_BOUNDS[z.id]))
      onZoneChange(zone?.id ?? null)
    }
    detect()
    map.on('move', detect)
    return () => map.off('move', detect)
  }, [map, onZoneChange])

  return null
}

// 追蹤目前地圖 zoom 值，供標籤文字判斷是否超過放大斷點
function ZoomTracker({ onZoomChange }) {
  const map = useMap()

  useEffect(() => {
    onZoomChange(map.getZoom())
  }, [map, onZoomChange])

  // 用 zoom（縮放進行中持續觸發，含手機 pinch 手勢過程）取代 zoomend（只在放開手指、縮放結束後觸發一次），
  // 讓遠景／近景畫面切換能即時跟著縮放手勢反應，不用等手勢結束
  useMapEvent('zoom', () => {
    onZoomChange(map.getZoom())
  })

  return null
}

function ZoneDetailCanvas({ onTableClick, onZoneButtonClick, onHongfaClick, flyToZone, flyToken, onZoneDetected, onZoomLevelChange, coordinateMap, targetTableId, forceLandscape, initialZoomOffset = 0, skipInitialZoomOffset = false, shouldKeepMax = false, isPortrait = false }) {
  const [hoveredId, setHoveredId] = useState(null)
  const [zoom, setZoom] = useState(-3)
  const isZoomedIn = zoom >= LABEL_ZOOM_BREAKPOINT
  const isOverview = !isZoomedIn
  const buttonScale = Math.pow(2, zoom + 1)

  useEffect(() => {
    onZoomLevelChange?.(isZoomedIn)
  }, [isZoomedIn, onZoomLevelChange])

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <MapContainer
        crs={L.CRS.Simple}
        bounds={IMAGE_BOUNDS}
        boundsOptions={{ padding: [0, 0] }}
        maxBounds={IMAGE_BOUNDS}
        maxBoundsViscosity={1.0}
        minZoom={-2}
        maxZoom={0.5}
        zoomSnap={0.25}
        zoomDelta={0.5}
        zoomControl={false}
        attributionControl={false}
        preferCanvas
        style={{ height: '100%', width: '100%', background: '#2A2A2A' }}
      >
        <MapResizeHandler />
        <RotatedDragHandler enabled={forceLandscape} />
        <MapZoomLimitHandler shouldKeepMax={shouldKeepMax} initialZoomOffset={initialZoomOffset} skipInitial={skipInitialZoomOffset} />
        <ZoneFlyTo flyToZone={flyToZone} flyToken={flyToken} isPortrait={isPortrait} />
        <ZoneDetector onZoneChange={onZoneDetected} />
        <ZoomTracker onZoomChange={setZoom} />
        <ImageOverlay url="/image/transmission/area_2.jpg" bounds={IMAGE_BOUNDS} />
        {isOverview ? (
          <>
            {/* 「弘法・現況」按鈕，點擊行為跟其他展區熱區一致：navigate 到展區二介紹頁 */}
            <Marker position={overviewToLatLng(1060, 640)} icon={EMPTY_ICON} interactive={false}>
              <Tooltip permanent direction="center" className="zone-label-tooltip zone-balloon-tooltip">
                <div
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    padding: '4px 6px',
                    cursor: 'pointer',
                    transform: `scale(${buttonScale})`,
                    transformOrigin: 'center center'
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    onHongfaClick()
                  }}
                >
                  {/* Top-Left Bracket */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '35%',
                    height: '45%',
                    borderTop: '1.5px solid #C5A059',
                    borderLeft: '1.5px solid #C5A059',
                    borderTopLeftRadius: '8px',
                    pointerEvents: 'none'
                  }} />
                  {/* Bottom-Right Bracket */}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: '35%',
                    height: '45%',
                    borderBottom: '1.5px solid #C5A059',
                    borderRight: '1.5px solid #C5A059',
                    borderBottomRightRadius: '8px',
                    pointerEvents: 'none'
                  }} />
                  {/* Inner Capsule */}
                  <div style={{
                    background: '#FFFFFF',
                    border: '1px solid #C5A059',
                    borderRadius: '6px',
                    padding: '4px 16px',
                    color: '#3D3A36',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    fontFamily: "'Noto Serif TC', serif",
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap'
                  }}>
                    弘法・現況
                  </div>
                </div>
              </Tooltip>
            </Marker>
            {ZONES.map(zone => (
              <Marker key={zone.id} position={overviewToLatLng(...OVERVIEW_ZONE_POINTS[zone.id])} icon={EMPTY_ICON} interactive={false}>
                <Tooltip permanent direction="center" className="zone-label-tooltip zone-balloon-tooltip">
                  {/* 這組氣球要比其他地方大 2 倍，用外層 span 縮放，避免動到共用的 .zone-balloon 基準尺寸 */}
                  <span style={{ display: 'inline-block', transform: 'scale(2)', transformOrigin: 'bottom center' }}>
                    <span
                      className="zone-balloon"
                      style={{ '--zone-label-color': OVERVIEW_ZONE_COLOR, cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        onZoneButtonClick(zone.id)
                      }}
                    >
                      <span className="zone-balloon__bubble">{zone.label}</span>
                      <span className="zone-balloon__tail" />
                    </span>
                  </span>
                </Tooltip>
              </Marker>
            ))}
          </>
        ) : (
          <>
            {TABLES.map(table => {
              const isHovered = hoveredId === table.id
              const monastery = coordinateMap[table.id]
              const isClickable = !!monastery?.has_exhibition_post
              const label = monastery?.monastery_abbr
              return (
                <Marker
                  key={table.id}
                  position={toLatLng(...table.point)}
                  icon={buildTableIcon(label, isHovered, isClickable, isZoomedIn)}
                  eventHandlers={isClickable ? {
                    click: () => onTableClick(table),
                    mouseover: () => setHoveredId(table.id),
                    mouseout: () => setHoveredId(null),
                  } : {}}
                />
              )
            })}
            {targetTableId && (() => {
              const targetTable = TABLES.find(t => t.id === targetTableId)
              if (!targetTable) return null
              return (
                <Marker
                  position={toLatLng(...targetTable.point)}
                  icon={buildTargetIcon()}
                  interactive={false}
                />
              )
            })()}
          </>
        )}
      </MapContainer>
    </div>
  )
}

export default function ZoneDetailMap({ onClose, initialRestoreState = null }) {
  const { t } = useLanguage()
  const navigate = useNavigate()
  // 地圖目前自由拖曳/縮放，activeZone 改為「偵測到地圖中心目前所在的分區」，用來讓分區按鈕即時反映位置，
  // 不再代表拖曳鎖定範圍
  const [activeZone, setActiveZone] = useState(null)
  // 分區按鈕被點擊時要飛過去的目標分區；用單獨的 state（而非沿用 activeZone）
  // 是因為每次點擊同一顆按鈕都要能重新觸發飛行動畫，即使 activeZone 沒有變化
  const [flyToZone, setFlyToZone] = useState(() => initialRestoreState?.activeZone ?? null)
  // 掛載當下是否已經指定要飛去某個分區的一次性快照，只在首次 render 求值，
  // 供 InitialZoomOffset 判斷要不要跳過「設回整圖置中 zoom」，避免跟掛載時的飛行動畫互相打架
  const hasInitialFlyTarget = useRef(!!initialRestoreState?.activeZone).current
  // flyToZone 不變（連續點同一顆分區按鈕）時，靠遞增 token 強制 ZoneFlyTo 重新觸發飛行動畫
  const [flyToken, setFlyToken] = useState(0)
  // 從報導頁定位回來時指定的桌位，用來在地圖上顯示跳動指示圖示；
  // 只在剛還原的當下顯示一次，使用者手動切換分區後應該消失，避免長期殘留造成誤解
  const [targetTableId, setTargetTableId] = useState(() => initialRestoreState?.selectedTableId ?? null)
  // table id -> { monastery_id, monastery_name, post_id, has_exhibition_post }
  const [coordinateMap, setCoordinateMap] = useState({})
  // 頂部法水長流文字按鈕只在近景（area_2.jpg，有桌位文字可看）時顯示；
  // 遠景（area_2.2.jpg）已經有地圖上的氣球可以點，不需要重複的按鈕列
  const [isZoomedIn, setIsZoomedIn] = useState(false)
  const isTouchDevice = useIsTouchDevice()
  // 強制橫向旋轉功能先關閉，恢復成原本正常方向；未來若要復原，取消下方兩行註解即可
  // const isPortrait = usePortraitOrientation()
  // const forceLandscape = isTouchDevice && isPortrait
  const forceLandscape = false
  const isPortrait = usePortraitOrientation()
  const [isMobileSize, setIsMobileSize] = useState(() => window.innerWidth < 768)

  useEffect(() => {
    const handleResize = () => {
      setIsMobileSize(window.innerWidth < 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const shouldKeepMax = isMobileSize || isTouchDevice || !isPortrait
  const initialZoomOffset = isPortrait ? ZONE_DETAIL_ZOOM_OFFSET_PORTRAIT : ZONE_DETAIL_ZOOM_OFFSET_LANDSCAPE

  useEffect(() => {
    getCoordinateList()
      .then(res => {
        const map = {}
        for (const item of res.items ?? []) {
          map[coordinateIdToTableId(item.coordinate_id)] = item
        }
        setCoordinateMap(map)
      })
      .catch(err => console.error('取得地圖點位對應精舍失敗', err))
  }, [])

  const handleTableClick = (table) => {
    const monastery = coordinateMap[table.id]
    if (!monastery?.has_exhibition_post) return
    navigate(ROUTES.TRANSMISSION_DETAIL(monastery.monastery_id), {
      state: {
        monasteryName: monastery.monastery_name,
        mapRestore: {
          isFullscreen: true,
          activeZoneId: 'zone2',
          zone2: { activeZone, selectedTableId: table.id },
        },
      },
    })
  }

  const handleZoneButtonClick = (id) => {
    setFlyToZone(id)
    setFlyToken(token => token + 1)
    setTargetTableId(null)
  }

  // area_2.2.jpg 上「弘法・現況」熱區跟外面第一層地圖的展區熱區行為一致：navigate 到展區二介紹頁；
  // 帶上來源標記，讓展區二介紹頁的返回鍵知道要重新打開全螢幕層 + ZoneDetailMap 遠景畫面（不指定分區，維持在 area_2.2.jpg）
  const handleHongfaClick = () => {
    navigate(ROUTES.TRANSMISSION_AREA(2), {
      state: { mapRestore: { from: 'hongfa-detail' } },
    })
  }

  return (
    <div
      className="fixed z-[2000]"
      style={
        forceLandscape
          ? {
            top: 0,
            left: '100%',
            right: 'auto',
            bottom: 'auto',
            height: '100dvw',
            width: '100dvh',
            background: '#2A2A2A',
            transform: 'rotate(90deg)',
            transformOrigin: 'top left',
          }
          : { top: 0, left: 0, right: 0, bottom: 0, height: '100dvh', width: '100dvw', background: '#2A2A2A' }
      }
    >
      <ZoneDetailCanvas
        onTableClick={handleTableClick}
        onZoneButtonClick={handleZoneButtonClick}
        onHongfaClick={handleHongfaClick}
        flyToZone={flyToZone}
        flyToken={flyToken}
        onZoneDetected={setActiveZone}
        onZoomLevelChange={setIsZoomedIn}
        coordinateMap={coordinateMap}
        targetTableId={targetTableId}
        forceLandscape={forceLandscape}
        isPortrait={isPortrait}
        initialZoomOffset={initialZoomOffset}
        skipInitialZoomOffset={hasInitialFlyTarget}
        shouldKeepMax={shouldKeepMax}
      />

      {isZoomedIn && (
        <div
          className="absolute left-1/2 top-4 z-[2100] flex gap-2 -translate-x-1/2"
          style={{ top: 'max(1rem, env(safe-area-inset-top))' }}
        >
          {ZONES.map(zone => (
            <button
              key={zone.id}
              onClick={() => handleZoneButtonClick(zone.id)}
              className={`w-10 h-10 rounded-full shadow-md flex items-center justify-center text-base font-bold font-['Noto_Serif_TC'] transition-colors cursor-pointer ${activeZone === zone.id ? 'bg-[#44842d] text-white' : 'bg-white/90 text-[#3D3A36] hover:bg-white'
                }`}
            >
              {zone.label}
            </button>
          ))}
        </div>
      )}

      <button
        onClick={onClose}
        aria-label={t('exhibition-map.closeAria')}
        className="absolute right-4 top-4 z-[2100] w-10 h-10 rounded-full bg-white/90 shadow-md flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
        style={{ top: 'max(1rem, env(safe-area-inset-top))', right: 'max(1rem, env(safe-area-inset-right))' }}
      >
        <X size={20} className="text-[#3D3A36]" />
      </button>
    </div>
  )
}
